import { useAtom } from 'jotai';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { RefObject, useCallback, useMemo } from 'react';

import useIsMobile from '../../../hooks/use-is-mobile';
import {
  brandAtom,
  modelAtom,
  versionAtom,
} from '../../../hooks/use-picker-selection';
import {
  currentSavedCarAtom,
  selectVehicleStepAtom,
  vehiclePickerOpen,
} from '../../../state/vehicle';
import TextLink from '../../ui/links/text';
import { useAppContext } from '../../providers/app-context';

const InvalidForm = ({
  errors,
  partSearchRef,
  savedVehicles,
  vehicleData,
  openCatalogueSidebar,
  referenceRef,
}: {
  errors: string[];
  partSearchRef: RefObject<HTMLInputElement>;
  savedVehicles: any;
  vehicleData: any;
  openCatalogueSidebar: any;
  referenceRef: RefObject<HTMLInputElement>;
}): JSX.Element => {
  const { t } = useTranslation();
  const { tenantConfig, brand } = useAppContext();
  const isMobile = useIsMobile();

  const [, setSelectedVehicleStep] = useAtom(selectVehicleStepAtom);
  const [, setVehiclePicker] = useAtom(vehiclePickerOpen);
  const [, setBrandAtom] = useAtom(brandAtom);
  const [, setModelAtom] = useAtom(modelAtom);
  const [, setVersionAtom] = useAtom(versionAtom);
  const [, setCurrentSavedCar] = useAtom(currentSavedCarAtom);

  const focusElement = useCallback(
    (element: 'reference' | 'part' | 'vehicle') => {
      setCurrentSavedCar(null);
      switch (element) {
        case 'part':
          if (isMobile) {
            openCatalogueSidebar();
          } else {
            partSearchRef.current && partSearchRef.current.focus();
          }
          return;
        case 'reference':
          referenceRef.current && referenceRef.current.focus();
          return;
        case 'vehicle':
          if ((savedVehicles && savedVehicles.length > 0) || (vehicleData?.info && vehicleData?.info.model)) {
            setSelectedVehicleStep('SELECT_SAVED_CAR');
            if (vehicleData?.info) setCurrentSavedCar(vehicleData?.info);
          } else {
            if (tenantConfig.id === 8 && brand) {
              setSelectedVehicleStep('SELECT_MODEL');
            } else {
              setSelectedVehicleStep('SELECT_BRAND');
            }
          }
          setVehiclePicker((prevValue) => !prevValue);
          setBrandAtom(null);
          setModelAtom(null);
          setVersionAtom(null);
          return;
        default:
          return;
      }
    },
    [
      isMobile,
      referenceRef,
      savedVehicles,
      setVehiclePicker,
      setBrandAtom,
      setModelAtom,
      setVersionAtom,
      openCatalogueSidebar,
      partSearchRef,
      setSelectedVehicleStep,
      setCurrentSavedCar,
      vehicleData?.info,
    ]
  );

  const referenceButton = useMemo(
    () => (
      <TextLink
        as="button"
        type="button"
        variant="PRIMARY"
        className="text-bparts-100 underline font-bold"
        onClick={() => focusElement('reference')}
        data-cy="invalid-form-add-reference"
      />
    ),
    [focusElement]
  );

  const vehicleButton = useMemo(
    () => (
      <TextLink
        as="button"
        type="button"
        variant="PRIMARY"
        className="text-bparts-100 underline font-bold"
        onClick={() => focusElement('vehicle')}
        data-cy="invalid-form-add-vehicle"
      />
    ),
    [focusElement]
  );

  const partButton = useMemo(
    () => (
      <TextLink
        as="button"
        type="button"
        variant="PRIMARY"
        className="text-bparts-100 underline font-bold"
        onClick={() => focusElement('part')}
        data-cy="invalid-form-add-part"
      />
    ),
    [focusElement]
  );

  const errorMessage = useMemo(() => {
    if (
      errors.includes('subCategory') &&
      errors.includes('vehicle') &&
      errors.includes('reference')
    ) {
      return (
        <p className="">
          <Trans
            i18nKey="products:complete-search.all"
            components={{
              reference: referenceButton,
              vehicle: vehicleButton,
              part: partButton,
            }}
          />
        </p>
      );
    }

    if (errors.includes('vehicle') && errors.includes('reference')) {
      return (
        <p className="">
          <Trans
            i18nKey="products:complete-search.reference-or-vehicle"
            components={{
              reference: referenceButton,
              vehicle: vehicleButton,
            }}
          />
        </p>
      );
    }

    if (errors.includes('subCategory') && errors.includes('reference')) {
      return (
        <p className="">
          <Trans
            i18nKey="products:complete-search.reference-or-part"
            components={{
              reference: referenceButton,
              part: partButton,
            }}
          />
        </p>
      );
    }
  }, [errors, partButton, referenceButton, vehicleButton]);

  return (
    <div className="lg:pb-8 max-lg:px-4 max-lg:text-left">
      <p className="font-extrabold text-[32px] mb-2">
        {t('products:searchChoice.sub-title')}
      </p>
      {errorMessage}
    </div>
  );
};

export default InvalidForm;
